import { useState } from 'react';
import { useSelector } from 'react-redux';
import getConfig from 'next/config';

import { CalendarEvent } from '@/components/calendar/utils/dates';
import { formatISODate, safeDifferenceInCalendarDays } from 'shared/util/datetime-fns';
import { trackCheckPriceAndAvailability } from '@/lib/tracking/tracking';
import { isDirectBookingForBrandEnabled } from '@/lib/helpers/importBrands';
import { useObject } from '@/hooks/useObject';
import { usePreview } from '@/hooks/usePreview';

import { Availability } from '../availability/Availability';
import { CalendarContactForm } from './CalendarContactForm';
import { CalendarContactFormNotTjvr } from './CalendarContactFormNotTjvr';
import { FhhState, UnleashToggles } from '@/domain';

import { CompactCalendar } from '@/components/compactCalendar/CompactCalendar';
import { useAvailabilityInfo } from '@/hooks/useAvailabilityInfo';

const {
    publicRuntimeConfig: { contextPath },
} = getConfig();

const generatePeriodParamsForUrl = ({
    startDate,
    endDate,
    ownerName,
}: {
    startDate: Date | null;
    endDate: Date | null;
    ownerName?: string;
}): string => {
    if (startDate && endDate && ownerName === 'DanCenter') {
        return `&firstdate=${formatISODate(startDate)?.replaceAll('-', '')}&days=${safeDifferenceInCalendarDays(startDate, endDate) ?? 1}`;
    }

    return '';
};

export interface Props {
    isLoggedIn: boolean;
    selectedDates: { from: Date | null; to: Date | null };
    unleash: UnleashToggles;
    today?: Date;
}

export function Calendar({ isLoggedIn, selectedDates: initialSelectedDates, unleash, today = new Date() }: Props) {
    const objectData = useObject();
    const { adId, bookings, isAdmin, isImport, owner, organisation, bookingEnabled } = objectData;
    const { data: availabilityInfo } = useAvailabilityInfo({ adId, isImmutable: true });

    if (!adId || !bookingEnabled) {
        return null;
    }

    const isPreview = usePreview();
    today.setHours(0, 0, 0, 0);
    initialSelectedDates?.from?.setHours(0, 0, 0, 0);
    initialSelectedDates?.to?.setHours(0, 0, 0, 0);
    const filters = useSelector((state: FhhState) => state.results.filters);
    const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(
        (filters?.booking_from ? new Date(filters.booking_from) : initialSelectedDates?.from) ?? null
    );
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(
        (filters?.booking_to ? new Date(filters.booking_to) : initialSelectedDates?.to) ?? null
    );
    const selectedDates = { from: selectedStartDate, to: selectedEndDate };

    const calendarEvents: CalendarEvent[] =
        bookings?.map((booking) => ({
            id: String(booking.bookingId),
            type: 'booking',
            startDate: new Date(booking.startDate),
            endDate: new Date(booking.endDate),
        })) ?? [];
    const deepLinkParams = generatePeriodParamsForUrl({ startDate: selectedStartDate, endDate: selectedEndDate, ownerName: owner?.name });

    const handleSelectChange = ({ startDate, endDate }: { startDate: Date | null; endDate: Date | null }) => {
        if (isPreview) {
            return;
        }

        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
    };

    const handleDateReset = () => {
        setSelectedStartDate(null);
        setSelectedEndDate(null);
    };

    const renderImportAdButton = () =>
        owner ? (
            <div>
                <a
                    className="button button--primary w-full max-w-full"
                    onClick={() => trackCheckPriceAndAvailability({ objectData })}
                    href={`${contextPath}/ut?finnkode=${adId}&deepLinkParams=${deepLinkParams !== '' ? btoa(deepLinkParams) : ''}`}
                    target="_blank"
                    rel="noopener noreferrer nofollow external"
                    data-testid="importAdButton">
                    Se pris og tilgjengelighet
                </a>
                {isImport && <div className="text-12 mt-8">OBS! I enkelte perioder kan man kun leie hele uker.</div>}
            </div>
        ) : null;

    return (
        <section className="space-y-16 my-16" data-testid="adCalendar" aria-labelledby="objectCalendarHeading">
            <CompactCalendar
                availabilityInfo={availabilityInfo}
                events={calendarEvents}
                onReset={handleDateReset}
                onSelectChange={handleSelectChange}
                selectedDates={selectedDates}
                today={today}
                title={
                    isDirectBookingForBrandEnabled(organisation?.importId, unleash)
                        ? 'Finn ledig tid i kalenderen'
                        : 'Velg tid i kalenderen'
                }
            />
            {isDirectBookingForBrandEnabled(organisation?.importId, unleash) ? (
                <Availability adId={adId} startDate={selectedStartDate} endDate={selectedEndDate} />
            ) : isImport || (isAdmin && owner?.deeplink) ? (
                renderImportAdButton()
            ) : unleash.tjvrRelease ? (
                <CalendarContactForm endDate={selectedEndDate} isLoggedIn={isLoggedIn} startDate={selectedStartDate} />
            ) : (
                <CalendarContactFormNotTjvr
                    closeForm={handleDateReset}
                    endDate={selectedEndDate}
                    isLoggedIn={isLoggedIn}
                    startDate={selectedStartDate}
                />
            )}
        </section>
    );
}
